import {
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

/**
 * Base class for a carousel component
 */
@Directive()
export abstract class CarouselBase<T> implements OnChanges {
  /**
   * List of items present in the carousel
   */
  @Input()
  public items: T[] = [];
  /** Amount of visible carousel indicators */
  @Input()
  public thumbnailsNumber = 4;

  @Input()
  public set selectIndex(index: number) {
    this.updateIndexes(index);
  }

  @Input()
  public showControls = true;

  @Input()
  public showMoreImagesIndicator = false;

  @Input()
  public moreImagesIndicatorTooltip: string;

  /** Output which emits the index of the curren variant */
  @Output()
  public clickedVariant: EventEmitter<number> = new EventEmitter();

  @Output()
  public activeChange: EventEmitter<number> = new EventEmitter();

  /** position of current selected item */
  public currentIndex = 0;
  /** offset in reference to the first item element visible, increased / decreased as the user navigates in the carousel. */
  public offSet = 0;

  /**
   * Returns the currently selected item.
   */
  public get currentItem() {
    return this.items?.[this.currentIndex];
  }

  /**
   * Updates the state of the component every time the inputs change.
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.items?.currentValue !== changes.items?.previousValue ||
      changes.thumbnailsNumber?.currentValue !==
        changes.thumbnailsNumber?.previousValue
    ) {
      this.updateIndexes();
    }
  }

  /**
   * Selects a item by position.
   * @param index
   */
  public indicatorClicked(index: number) {
    this.currentIndex = index + this.offSet;
    this.clickedVariant.emit(index);
  }

  /**
   * Selects the next available item.
   */
  public selectNext(event: Event) {
    event.stopPropagation();
    this.updateIndexes(Math.min(this.currentIndex + 1, this.items?.length - 1));
  }

  /**
   * Selects the previous available item.
   */
  public selectPrevious(event: Event) {
    event.stopPropagation();
    this.updateIndexes(Math.max(this.currentIndex - 1, 0));
  }

  /**
   * Update properties tied to indexes for the carousel: currentIndex and offSet
   * @param newIndex
   */
  public updateIndexes(newIndex?: number) {
    if (this.currentIndex > this.items?.length - 1) {
      this.currentIndex = 0;
    }

    this.currentIndex = newIndex ?? this.currentIndex;
    const newOffSet = Math.max(
      this.currentIndex - this.thumbnailsNumber + 1,
      0,
    );
    if (newOffSet !== this.offSet) {
      this.offSet = newOffSet;
      this.offsetChanged();
    }

    this.activeChange.emit(this.currentIndex);
  }

  protected offsetChanged(): void {}

  /**
   * returns true if the button to navigate to the next element is enabled
   */
  public isNextEnabled() {
    return (
      this.currentIndex < this.items?.length - 1 && this.showNavigationArrows()
    );
  }

  /**
   * returns true if the button to navigate to the previous element is enabled
   */
  public isPreviousEnabled() {
    return this.currentIndex > 0 && this.showNavigationArrows();
  }
  /**
   * returns true if the number of variants is greter than the number of visible thumbnails.
   */
  private showNavigationArrows() {
    return this.thumbnailsNumber < this.items?.length;
  }

  /**
   * Returns true if the indicator index corresponds to an item that is currently active in the carousel.
   * @param indicatorIndex current index of the thumbnail in visible the carousel
   */
  public isActive(indicatorIndex: number) {
    return indicatorIndex + this.offSet === this.currentIndex;
  }

  public getOverflow(): number {
    return this.items?.length - this.thumbnailsNumber;
  }

  /**
   * Returns the array of carousel indicators to be shown.
   */
  public abstract getIndicators(): unknown[];
}
